/**
 * NewProductModal — onboarding for an OWN-PRODUCT design, launched from the FORGE
 * cockpit. Name it, drop a spec PDF (Claude extracts the line items) and/or add
 * items by hand, then "Send to sourcing" — which materializes a FORGE spec sheet
 * and drops the product straight into the cockpit list as a sourceable row.
 *
 * The design is created lazily on the first action (upload / add line) so bailing
 * out immediately doesn't leave an empty draft.
 */
import React, { useCallback, useRef, useState } from 'react';
import { X, UploadCloud, Plus, Trash2, Loader2, ArrowRight, ImagePlus, Sparkles } from 'lucide-react';
import { forgeAPI } from '../../../services/api';

const emptyLine = { product_name: '', contract_quantity: '', certs: '' };

const NewProductModal = ({ onClose, onSourced }) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [productId, setProductId] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [manual, setManual] = useState(emptyLine);
  const [images, setImages] = useState([]);    // [{img_id, url}]
  const [busy, setBusy] = useState('');        // '', 'extracting', 'sourcing', 'adding', 'imaging'
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const imgRef = useRef(null);

  // Create the design on first need, reusing the current name/category.
  const ensureProduct = useCallback(async () => {
    if (productId) return productId;
    const d = await forgeAPI.createProduct({
      name: name.trim(), category: category.trim(),
    });
    setProductId(d.product_id);
    return d.product_id;
  }, [productId, name, category]);

  const applyDesign = (design) => {
    setLineItems(design?.line_items || []);
    if (!name && design?.name) setName(design.name);
  };

  const craftSpec = async () => {
    if (!description.trim()) return;
    setError(''); setBusy('crafting');
    try {
      const id = await ensureProduct();
      const res = await forgeAPI.craftProduct(id, description.trim());
      applyDesign(res.design);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not craft the spec — try again.');
    } finally { setBusy(''); }
  };

  const onImages = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith('image/'));
    if (!list.length) return;
    setError(''); setBusy('imaging');
    try {
      const id = await ensureProduct();
      for (const f of list) await forgeAPI.uploadProductImage(id, f, 0);
      const res = await forgeAPI.listProductImages(id);
      setImages(res.images || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not attach the image(s).');
    } finally { setBusy(''); if (imgRef.current) imgRef.current.value = ''; }
  };

  const onFile = async (file) => {
    if (!file) return;
    setError('');
    setBusy('extracting');
    try {
      const id = await ensureProduct();
      const res = await forgeAPI.uploadProductSpec(id, file, false);
      applyDesign(res.design);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Could not read that PDF.');
    } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const addManual = async () => {
    if (!manual.product_name.trim()) return;
    setError('');
    setBusy('adding');
    try {
      const id = await ensureProduct();
      const design = await forgeAPI.addProductLine(id, {
        product_name: manual.product_name.trim(),
        contract_quantity: manual.contract_quantity ? Number(manual.contract_quantity) : null,
        required_certifications: manual.certs
          ? manual.certs.split(',').map((s) => s.trim()).filter(Boolean) : [],
      });
      applyDesign(design);
      setManual(emptyLine);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not add the line item.');
    } finally {
      setBusy('');
    }
  };

  const removeLine = async (n) => {
    if (!productId) return;
    try {
      const design = await forgeAPI.deleteProductLine(productId, n);
      applyDesign(design);
    } catch { /* keep the row on failure */ }
  };

  // Patch a field on blur (name / qty / certs), re-render from the server's renumber.
  const patchLine = async (n, updates) => {
    if (!productId) return;
    try {
      const design = await forgeAPI.patchProductLine(productId, n, updates);
      applyDesign(design);
    } catch { /* leave the edit as-is on failure */ }
  };

  const canSource = name.trim() && lineItems.length > 0 && !busy;

  const sendToSourcing = async () => {
    if (!canSource) return;
    setError('');
    setBusy('sourcing');
    try {
      const id = await ensureProduct();
      if (name.trim() || category.trim()) {
        await forgeAPI.updateProduct(id, { name: name.trim(), category: category.trim() });
      }
      const res = await forgeAPI.sendProductToSourcing(id);
      onSourced(res.tender_id || id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send to sourcing.');
      setBusy('');
    }
  };

  return (
    <div className="forge-np-backdrop" onClick={onClose}>
      <div className="forge-np" onClick={(e) => e.stopPropagation()}>
        <div className="forge-np-head">
          <span style={{ fontWeight: 700 }}>New product to source</span>
          <button className="forge-btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="forge-np-body">
          {/* Details */}
          <div className="forge-np-row">
            <label className="forge-np-field" style={{ flex: 2 }}>
              <span>Product name</span>
              <input value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Emergency Signal Kit" autoFocus />
            </label>
            <label className="forge-np-field" style={{ flex: 1 }}>
              <span>Category <span className="forge-muted">(optional)</span></span>
              <input value={category} onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Safety Equipment" />
            </label>
          </div>

          {/* Describe → craft: let an LLM build the spec from a description + attachments */}
          <div className="forge-np-field">
            <span>Describe what you're making <span className="forge-muted">— we'll craft the spec</span></span>
            <textarea className="forge-np-describe" rows={3} value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. 300 pairs of heavy-duty insulated waterproof winter work gloves meeting EN 388, sizes M–XL, reinforced palms…" />
            <button type="button" className="forge-np-craft" onClick={craftSpec}
              disabled={!description.trim() || busy === 'crafting'}>
              {busy === 'crafting'
                ? (<><Loader2 size={15} className="forge-spin" /> Crafting the spec…</>)
                : (<><Sparkles size={15} /> Craft the spec {(images.length || lineItems.length) ? '(uses your attachments)' : ''}</>)}
            </button>
          </div>

          <div className="forge-np-or"><span>or</span></div>

          {/* Spec PDF dropzone */}
          <div
            className="forge-np-drop"
            onClick={() => !busy && fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}>
            {busy === 'extracting'
              ? (<><Loader2 size={18} className="forge-spin" /> Extracting line items…</>)
              : (<><UploadCloud size={18} /> Drop a spec PDF here or click to upload
                  <span className="forge-muted"> — we extract the line items for you</span></>)}
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" hidden
              onChange={(e) => onFile(e.target.files?.[0])} />
          </div>

          {/* Line items */}
          <div className="forge-np-items">
            <div className="forge-np-items-hd">
              Line items {lineItems.length ? `· ${lineItems.length}` : ''}
            </div>
            {lineItems.length === 0 && (
              <div className="forge-muted" style={{ fontSize: 13, padding: '6px 2px' }}>
                No line items yet — upload a spec PDF or add one below.
              </div>
            )}
            {lineItems.map((li) => (
              <div key={li.line_item} className="forge-np-li">
                <span className="forge-np-li-n">{li.line_item}</span>
                <input className="forge-np-li-name" defaultValue={li.product_name}
                  onBlur={(e) => e.target.value !== li.product_name
                    && patchLine(li.line_item, { product_name: e.target.value })} />
                <input className="forge-np-li-qty" type="number" defaultValue={li.contract_quantity ?? ''}
                  placeholder="qty"
                  onBlur={(e) => patchLine(li.line_item,
                    { contract_quantity: e.target.value ? Number(e.target.value) : null })} />
                <input className="forge-np-li-certs" defaultValue={(li.required_certifications || []).join(', ')}
                  placeholder="certs"
                  onBlur={(e) => patchLine(li.line_item, {
                    required_certifications: e.target.value
                      ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean) : [],
                  })} />
                <button className="forge-btn-ghost" title="Remove"
                  onClick={() => removeLine(li.line_item)}><Trash2 size={14} /></button>
              </div>
            ))}

            {/* Manual add row */}
            <div className="forge-np-li forge-np-add">
              <span className="forge-np-li-n"><Plus size={13} /></span>
              <input className="forge-np-li-name" placeholder="Add a line item…"
                value={manual.product_name}
                onChange={(e) => setManual({ ...manual, product_name: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && addManual()} />
              <input className="forge-np-li-qty" type="number" placeholder="qty"
                value={manual.contract_quantity}
                onChange={(e) => setManual({ ...manual, contract_quantity: e.target.value })} />
              <input className="forge-np-li-certs" placeholder="certs (comma)"
                value={manual.certs}
                onChange={(e) => setManual({ ...manual, certs: e.target.value })} />
              <button className="forge-btn-ghost" title="Add" onClick={addManual}
                disabled={!manual.product_name.trim() || busy === 'adding'}>
                {busy === 'adding' ? <Loader2 size={14} className="forge-spin" /> : <Plus size={14} />}
              </button>
            </div>
          </div>

          {/* Reference images — ride into sourcing so Accio can share them with suppliers */}
          <div className="forge-np-items">
            <div className="forge-np-items-hd">
              Reference images {images.length ? `· ${images.length}` : ''} <span className="forge-muted">(optional)</span>
            </div>
            <div className="forge-np-imgs">
              {images.map((im) => (
                <img key={im.img_id} className="forge-np-img" src={im.url} alt="reference"
                  onClick={() => window.open(im.url, '_blank')} />
              ))}
              <button type="button" className="forge-np-img-add" onClick={() => imgRef.current?.click()}
                disabled={busy === 'imaging'}>
                {busy === 'imaging' ? <Loader2 size={16} className="forge-spin" /> : <ImagePlus size={18} />}
                <span>Add images</span>
              </button>
              <input ref={imgRef} type="file" accept="image/*" multiple hidden
                onChange={(e) => onImages(e.target.files)} />
            </div>
          </div>

          {error && <div className="forge-np-err">{error}</div>}
        </div>

        <div className="forge-np-foot">
          <button className="forge-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="forge-btn-primary" disabled={!canSource} onClick={sendToSourcing}>
            {busy === 'sourcing'
              ? (<><Loader2 size={14} className="forge-spin" /> Sending…</>)
              : (<>Send to sourcing <ArrowRight size={14} /></>)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewProductModal;
